import type { BottleUnit } from "@babia/checks";
import { X } from "lucide-react";
import {
  entryDetail,
  type EntryLike,
  entryTitle,
  TYPE_DOT,
  type WeightShown,
} from "../../lib/entry-text.ts";
import { type Entry, isPending } from "../../lib/queries.ts";
import { type EntryChange, markText, removedText, withoutRemoved } from "../../lib/revisions.ts";
import { clock, isSameLocalDay, pluralize } from "../../lib/time.ts";
import { byDay } from "../../lib/timeline.ts";
import { cn } from "../../lib/utils.ts";
import { Button } from "../ui/button.tsx";

/**
 * A saved row can be removed with ×, then restored from the Undo toast. A tap
 * on its title or detail opens the edit sheet. With no `onRemove` and no
 * `onEdit` (a read-only log after the trial) the row has neither.
 */
function Row({
  entry,
  unit,
  weight,
  change,
  now,
  onRemove,
  onEdit,
  onChange,
}: {
  entry: Entry;
  unit: BottleUnit;
  weight?: WeightShown;
  /** An assistant's edit to this entry that can still be put back. */
  change?: EntryChange;
  now: Date;
  onRemove?: (e: Entry) => void;
  onEdit?: (e: Entry) => void;
  onChange?: (c: EntryChange) => void;
}) {
  const pending = isPending(entry);
  const label = `${entryTitle(entry)} at ${clock(entry.at)}`;
  const text = (
    <>
      <span className="heading block text-[16px]">{entryTitle(entry)}</span>
      <span className="block break-words text-[13px] opacity-65">
        {entryDetail(entry, unit, weight)}
      </span>
    </>
  );

  return (
    <li className="flex items-start gap-3 border-t border-rule bg-paper py-[11px]">
      <div className="w-[58px] shrink-0 pt-0.5 text-[13px] tabular-nums opacity-70">
        {clock(entry.at)}
      </div>
      <div
        className={cn("mt-2 size-[7px] shrink-0 rounded-full", TYPE_DOT[entry.type])}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        {onEdit ? (
          // No icon: the row keeps its clean look, and the text is the target.
          <button
            type="button"
            onClick={() => onEdit(entry)}
            disabled={pending}
            aria-haspopup="dialog"
            className="-my-1 block w-full py-1 text-left disabled:cursor-wait"
          >
            <span className="sr-only">Edit </span>
            {text}
          </button>
        ) : (
          text
        )}
        {change && onChange ? (
          <button
            type="button"
            onClick={() => onChange(change)}
            className="-mb-2 -ml-1 flex min-h-[44px] items-center px-1 text-left text-[13px] text-accent"
          >
            {markText(change, now)}&nbsp;›
          </button>
        ) : null}
      </div>
      {onRemove ? (
        <button
          type="button"
          onClick={() => onRemove(entry)}
          disabled={pending}
          className="-my-1 -mr-1 flex size-11 shrink-0 items-center justify-center self-center disabled:cursor-wait disabled:opacity-40"
          aria-label={`Remove ${label}`}
          title="Remove"
        >
          <span className="flex size-7 items-center justify-center rounded-full bg-danger-tint text-danger">
            <X className="size-4" strokeWidth={2.5} />
          </span>
        </button>
      ) : null}
    </li>
  );
}

/**
 * An entry an assistant removed, at the entry's time, with Put it back (#72).
 * Without `onPutBack` (a read-only log) it only shows.
 */
function RemovedRow({
  change,
  unit,
  weight,
  now,
  putting,
  onPutBack,
}: {
  change: EntryChange;
  unit: BottleUnit;
  weight?: WeightShown;
  now: Date;
  putting: boolean;
  onPutBack?: (c: EntryChange) => void;
}) {
  const entry = change.before;
  return (
    <li className="flex items-start gap-3 border-t border-rule bg-paper py-[11px]">
      <div className="w-[58px] shrink-0 pt-0.5 text-[13px] tabular-nums opacity-45">
        {clock(entry.at)}
      </div>
      <div
        className={cn("mt-2 size-[7px] shrink-0 rounded-full opacity-35", TYPE_DOT[entry.type])}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <div className="heading text-[16px] line-through opacity-45">{entryTitle(entry)}</div>
        <div className="break-words text-[13px] opacity-65">
          {[entryDetail(entry, unit, weight), removedText(change, now)].filter(Boolean).join(" · ")}
        </div>
      </div>
      {onPutBack ? (
        <button
          type="button"
          onClick={() => onPutBack(change)}
          disabled={putting}
          className="ghost-btn -my-1 min-h-[44px] shrink-0 self-center px-2 text-[14px] disabled:cursor-wait disabled:opacity-40"
        >
          {putting ? "Putting back…" : "Put it back"}
        </button>
      ) : null}
    </li>
  );
}

/** A timeline row: a logged entry, or one an assistant removed. */
type Item = { at: Date; entry: Entry } | { at: Date; removed: EntryChange };

const newestFirst = (a: Item, b: Item) => b.at.getTime() - a.at.getTime();

/** Where "Show earlier entries" stands for this baby. */
export interface EarlierState {
  /** A page is on its way. */
  loading: boolean;
  /** Every entry back to the baby's first is shown. */
  done: boolean;
  error: string | null;
  onShow: () => void;
}

export function Timeline({
  babyName,
  unit,
  weightOf,
  today,
  earlier,
  older,
  more,
  now,
  changeOf,
  removed = [],
  putting = null,
  onRemove,
  onEdit,
  onChange,
  onPutBack,
  className,
}: {
  babyName: string;
  /** The unit bottles show in. */
  unit: BottleUnit;
  /** How a weight's number reads: its unit and percentile. */
  weightOf?: (e: EntryLike) => WeightShown;
  today: Entry[];
  /** The live list's entries before today. */
  earlier: Entry[];
  /** Entries older than the live list holds, from "Show earlier entries". */
  older: Entry[];
  more: EarlierState;
  now: Date;
  /** An assistant's edit to an entry that can still be put back. */
  changeOf?: (e: Entry) => EntryChange | undefined;
  /** This baby's entries an assistant removed, shown at their times. */
  removed?: readonly EntryChange[];
  /** The change being put back. */
  putting?: string | null;
  /** Omitted when the log is read-only. */
  onRemove?: (e: Entry) => void;
  /** Opens the edit sheet; omitted when the log is read-only. */
  onEdit?: (e: Entry) => void;
  onChange?: (c: EntryChange) => void;
  /** Omitted when the log is read-only. */
  onPutBack?: (c: EntryChange) => void;
  className?: string;
}) {
  // A removed row replaces its entry's row, which an older page can still hold.
  const asItems = (list: readonly Entry[]): Item[] =>
    withoutRemoved(list, removed).map((entry) => ({ at: entry.at, entry }));
  const removedItems: Item[] = removed.map((c) => ({ at: c.before.at, removed: c }));
  const todayItems = [
    ...asItems(today),
    ...removedItems.filter((i) => isSameLocalDay(i.at, now)),
  ].sort(newestFirst);
  const days = byDay(
    [
      ...asItems([...earlier, ...older]),
      ...removedItems.filter((i) => !isSameLocalDay(i.at, now)),
    ].sort(newestFirst),
    now,
  );
  const rows = (list: Item[]) =>
    list.map((item) =>
      "entry" in item ? (
        <Row
          key={item.entry.id}
          entry={item.entry}
          unit={unit}
          weight={weightOf?.(item.entry)}
          change={changeOf?.(item.entry)}
          now={now}
          onRemove={onRemove}
          onEdit={onEdit}
          onChange={onChange}
        />
      ) : (
        <RemovedRow
          key={`removed-${item.removed.id}`}
          change={item.removed}
          unit={unit}
          weight={weightOf?.(item.removed.before)}
          now={now}
          putting={putting === item.removed.id}
          onPutBack={onPutBack}
        />
      ),
    );

  return (
    <section className={cn("mt-[30px]", className)} aria-label={`${babyName} timeline`}>
      <div className="flex items-baseline">
        <h2 className="kicker">{babyName} — today</h2>
        <span className="ml-auto text-[12px] opacity-55">
          {pluralize(today.length, "entry", "entries")}
        </span>
      </div>
      {todayItems.length === 0 ? (
        <p className="py-[26px] text-[14px] italic opacity-60">Nothing logged yet today.</p>
      ) : (
        <ul className="mt-2.5">{rows(todayItems)}</ul>
      )}
      {days.map((day) => (
        <div key={day.start.getTime()}>
          <h2 className="kicker mt-6">{day.label}</h2>
          <ul className="mt-2.5">{rows(day.entries)}</ul>
        </div>
      ))}
      <div className="mt-6 border-t border-rule pt-4">
        {more.error ? (
          <p className="mb-3 text-[14px] text-accent-2">
            Couldn't load earlier entries — {more.error}
          </p>
        ) : null}
        {more.done ? (
          <p className="text-center text-[13px] italic opacity-60">
            That's everything logged for {babyName}.
          </p>
        ) : (
          <Button
            variant="secondary"
            className="w-full"
            disabled={more.loading}
            onClick={more.onShow}
          >
            {more.loading ? "Loading…" : more.error ? "Try again" : "Show earlier entries"}
          </Button>
        )}
      </div>
    </section>
  );
}
